import { Dispatch, ReactNode, Ref, SetStateAction, memo, useCallback } from "react";
import { usePartialSubmitForOverview } from "src/apps/AppFlow/pages/hooks";
import { FlowLayoutItemsOverview } from "src/components";
import { ItemControl } from "src/hooks/useSubpages";
import { IncomeSource } from "src/types/Applicant";
import { BlockFocus } from "src/utilities/react";

import { enums, formatters } from "@fraction/shared";

const { INCOME_TYPE_MAP } = formatters.enums;

interface IncomeSourcesProps {
  value?: ItemControl<IncomeSource>;
  onChange: Dispatch<SetStateAction<ItemControl<IncomeSource> | undefined>>;
  onNext: () => void;
  onBack: () => void;
  focusRef?: Ref<HTMLDivElement>;
  title?: string;
  children?: ReactNode;
  // used by FlowLayout component
  blockFocus?: BlockFocus;
}

const IncomeSources = ({
  value,
  onChange,
  onNext,
  onBack,
  focusRef,
  title,
  children,
  ...props
}: IncomeSourcesProps) => {
  const { handleNext, nextDisabled, nextLoading } = usePartialSubmitForOverview({ value, onNext });

  const headerFormatter = useCallback(
    (incomeSource: IncomeSource) =>
      incomeSource?.incomeSourceType ? INCOME_TYPE_MAP[incomeSource.incomeSourceType] : "",
    []
  );

  const subheaderFormatter = useCallback((incomeSource: IncomeSource) => {
    if (incomeSource?.incomeSourceType === enums.IncomeType.EMPLOYMENT_INCOME) {
      return incomeSource?.employerDetails?.name || "Unknown";
    }
    return "";
  }, []);

  const descriptionFormatter = useCallback(
    (incomeSource: IncomeSource) =>
      formatters.number.getCurrencyFromNumber(incomeSource?.incomeDetails?.income, {
        inputInDollars: true,
      }),
    []
  );

  return (
    <FlowLayoutItemsOverview
      {...props}
      focusRef={focusRef}
      newItemTemplate={{}}
      itemControl={value}
      exampleHeader="Income type"
      exampleSubheader="Example"
      exampleDescription="$35,000.00"
      addItemButtonText="Add income source"
      headerFormatter={headerFormatter}
      subheaderFormatter={subheaderFormatter}
      descriptionFormatter={descriptionFormatter}
      onNext={handleNext}
      onBack={onBack}
      onChange={onChange}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
      title={title || "Your sources of income"}
      description="Please provide more details on any sources of income that you have."
      skipButtonText="I have no other income sources"
    >
      {children}
    </FlowLayoutItemsOverview>
  );
};

export default memo(IncomeSources);
